import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';
import { RootState } from '../..';
import {
  adminGetZoomMeetingRequestById,
  adminGetZoomMeetingRequests,
  adminRespondZoomMeetingRequest,
  adminUpdateZoomMeetingRequest,
} from './adminApi';
import { IUser } from '../../UserSlice';
import { getZoomMeetingRequests } from './api';

export type TZoomMeetingRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
export type TZoomMeetingRequestType = 'CANCEL' | 'RESCHEDULE' | 'DELETE';
export type TZoomMeetingRequestRequestedByRole = 'STUDENT' | 'TUTOR' | 'ADMIN';

export interface IZoomMeetingRequest {
  _id: string;
  course: string;
  section: string;
  status: TZoomMeetingRequestStatus;
  type: TZoomMeetingRequestType;
  start: string;
  requestedBy: Partial<IUser>;
  requestedByRole: TZoomMeetingRequestRequestedByRole;
  reason: string;
  createdAt: string;
  tutor?: IUser;
  student?: IUser;
  newStart?: string;
  penalty?: number;
  penaltyReason?: string;
  penaltyMessage?: string;
  adminNote?: string;
}

export interface IInitialZoomRequestsState {
  status: 'idle' | 'loading' | 'rejected';
  zoomRequestListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IZoomMeetingRequest>;
  };

  // ADMIN
  adminZoomRequestListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IZoomMeetingRequest>;
  };
  adminZoomRequestData?: IZoomMeetingRequest;
}

export const ZOOM_MEETING_REQUEST_STATUSES = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
};

export const ZOOM_MEETING_REQUEST_TYPES = {
  CANCEL: 'CANCEL',
  RESCHEDULE: 'RESCHEDULE',
  DELETE: 'DELETE',
};

export const ZOOM_MEETING_REQUEST_REQUESTED_BY_ROLES = {
  STUDENT: 'STUDENT',
  TUTOR: 'TUTOR',
  ADMIN: 'ADMIN',
};

const initialState: IInitialZoomRequestsState = {
  status: 'idle',
  zoomRequestListData: undefined,
  // ADMIN
  adminZoomRequestListData: undefined,
  adminZoomRequestData: undefined,
};

export const ZoomRequestsSlice = createSlice({
  name: 'zoomRequests',
  initialState,
  reducers: {
    resetZoomRequestListData: (state) => {
      state.zoomRequestListData = undefined;
    },
    // ADMIN
    resetAdminZoomRequestListData: (state) => {
      state.adminZoomRequestListData = undefined;
    },
    resetAdminZoomRequestData: (state) => {
      state.adminZoomRequestData = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getZoomMeetingRequests.fulfilled, (state, action) => {
      const result = action.payload;

      const newZoomRequestListData = {
        limit: result.limit,
        offset: result.offset,
        totalDocs: result.totalDocs || 0,
        list: result.docs || [],
      };

      state.zoomRequestListData = newZoomRequestListData;
    });

    // ADMIN APIS
    builder
      .addCase(adminGetZoomMeetingRequests.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminZoomRequestListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminZoomRequestListData = newAdminZoomRequestListData;
      })
      .addCase(adminGetZoomMeetingRequestById.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminZoomRequestData = result;
      })
      .addCase(adminRespondZoomMeetingRequest.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminZoomRequestData = result;

        if (
          state.adminZoomRequestListData &&
          state.adminZoomRequestListData.list &&
          state.adminZoomRequestListData.list.length
        ) {
          const newAdminZoomRequestListData = {
            ...state.adminZoomRequestListData,
          };

          newAdminZoomRequestListData.list = newAdminZoomRequestListData.list.filter(
            (item) => item._id !== result._id
          );

          if (
            newAdminZoomRequestListData.totalDocs &&
            newAdminZoomRequestListData.totalDocs > 0
          ) {
            newAdminZoomRequestListData.totalDocs -= 1;
          }

          state.adminZoomRequestListData = newAdminZoomRequestListData;
        }
      })
      .addCase(adminUpdateZoomMeetingRequest.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminZoomRequestData = result;
      });

    // Common Matchers
    builder.addMatcher(
      isPending(
        adminGetZoomMeetingRequests,
        adminGetZoomMeetingRequestById,
        adminRespondZoomMeetingRequest,
        adminUpdateZoomMeetingRequest,
        getZoomMeetingRequests
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isRejected(
        adminGetZoomMeetingRequests,
        adminGetZoomMeetingRequestById,
        adminRespondZoomMeetingRequest,
        adminUpdateZoomMeetingRequest,
        getZoomMeetingRequests
      ),
      (state) => {
        state.status = 'rejected';
      }
    );

    builder.addMatcher(
      isFulfilled(
        adminGetZoomMeetingRequests,
        adminGetZoomMeetingRequestById,
        adminRespondZoomMeetingRequest,
        adminUpdateZoomMeetingRequest,
        getZoomMeetingRequests
      ),
      (state) => {
        state.status = 'idle';
      }
    );
  },
});

export const getZoomRequestsState = (
  state: RootState
): RootState['zoomRequests'] => state.zoomRequests;

export const {
  resetAdminZoomRequestListData,
  resetAdminZoomRequestData,
  resetZoomRequestListData,
} = ZoomRequestsSlice.actions;

export * from './adminApi';
export default ZoomRequestsSlice.reducer;
